import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DateValue, ErrorState } from '@/components/common'
import {
  CredentialPanel, OverrideDialog, useCredentialCheck, useOverrideMutations,
} from '@/features/compliance'
import type { CredentialFailureView } from '@/features/compliance'
import { usePermission } from '@/hooks/usePermission'
import { toApiProblem } from '@/lib/api/client'
import type { AssignmentView, TransferType } from '../types'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

export interface TransferDraft {
  employeeId: string
  transferType: TransferType
  effectiveFrom: string
  plannedEndDate: string | null
  toDepartmentId: string | null
  toLocationId: string | null
  toDesignationId: string | null
  toGradeId: string | null
  toCostCenterId: string | null
  toManagerEmployeeId: string | null
  reason: string | null
}

interface Option {
  id: string
  name: string
}

const STEPS = ['Where to', 'When', 'Confirm'] as const

/**
 * Raising a transfer.
 *
 * <p>Three steps because a transfer is three decisions, and putting them on one form makes
 * the important one — the date — look like an afterthought next to a dozen dropdowns. The
 * date is what decides which month's cost the move lands in, so it gets a step to itself.
 *
 * <p>Only fields that are actually being changed are sent. Anything left alone carries
 * forward from the current placement, so a cost-centre move does not silently restate the
 * department as whatever happened to be preselected.
 */
export function TransferWizard({
  employeeId,
  employeeName,
  current,
  departments,
  locations,
  designations,
  grades,
  costCenters,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  employeeId: string
  employeeName: string
  current: AssignmentView | undefined
  departments: Option[]
  locations: Option[]
  designations: Option[]
  grades: Option[]
  costCenters: Option[]
  submitting: boolean
  error: unknown
  onSubmit: (draft: TransferDraft) => void
  onCancel: () => void
}) {
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<TransferDraft>({
    employeeId,
    transferType: 'PERMANENT',
    effectiveFrom: todayLocal(),
    plannedEndDate: null,
    toDepartmentId: null,
    toLocationId: null,
    toDesignationId: null,
    toGradeId: null,
    toCostCenterId: null,
    toManagerEmployeeId: null,
    reason: null,
  })

  function set<K extends keyof TransferDraft>(key: K, value: TransferDraft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }))
  }

  // Only asked for on the confirm step. Running it while somebody is still choosing a
  // department would fire a request per keystroke of the form and, worse, show a red
  // "blocked" panel against a destination they have not settled on.
  const credentials = useCredentialCheck(
    step === 2
      ? {
          employeeId,
          locationId: draft.toLocationId,
          gradeId: draft.toGradeId,
          designationId: draft.toDesignationId,
          asOf: draft.effectiveFrom,
        }
      : null,
  )
  const overrides = useOverrideMutations()
  const [overriding, setOverriding] = useState<CredentialFailureView | null>(null)
  const [overrideError, setOverrideError] = useState<string | null>(null)
  const canOverride = usePermission('compliance:override')

  const blocked = (credentials.data?.blocking.length ?? 0) > 0

  const changes = describeChanges(draft, current, {
    departments,
    locations,
    designations,
    grades,
    costCenters,
  })
  const nothingChanged = changes.length === 0
  const needsEndDate = draft.transferType === 'TEMPORARY' && !draft.plannedEndDate
  const endBeforeStart =
    draft.plannedEndDate !== null && draft.plannedEndDate <= draft.effectiveFrom

  return (
    <Card className="p-5">
      <ol className="mb-5 flex items-center gap-2 text-sm" aria-label="Transfer steps">
        {STEPS.map((label, index) => (
          <li key={label} className="flex items-center gap-2">
            <span
              className={cn(
                'grid size-6 place-items-center rounded-full text-xs',
                index < step && 'bg-[var(--color-success)] text-white',
                index === step && 'bg-[var(--color-primary)] text-white',
                index > step && 'bg-[var(--color-surface)] text-[var(--color-muted)]',
              )}
              aria-current={index === step ? 'step' : undefined}
            >
              {index < step ? <Check className="size-3" aria-hidden="true" /> : index + 1}
            </span>
            <span className={cn(index === step ? 'text-[var(--color-heading)]' : 'text-[var(--color-muted)]')}>
              {label}
            </span>
            {index < STEPS.length - 1 && (
              <ArrowRight className="size-3 text-[var(--color-muted)]" aria-hidden="true" />
            )}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Picker
            id="to-department"
            label="Department"
            options={departments}
            currentName={current?.departmentName}
            value={draft.toDepartmentId}
            onChange={(value) => set('toDepartmentId', value)}
          />
          <Picker
            id="to-location"
            label="Site"
            options={locations}
            currentName={current?.locationName}
            value={draft.toLocationId}
            onChange={(value) => set('toLocationId', value)}
          />
          <Picker
            id="to-designation"
            label="Designation"
            options={designations}
            currentName={current?.designationName}
            value={draft.toDesignationId}
            onChange={(value) => set('toDesignationId', value)}
          />
          <Picker
            id="to-grade"
            label="Grade"
            options={grades}
            currentName={current?.gradeName}
            value={draft.toGradeId}
            onChange={(value) => set('toGradeId', value)}
          />
          <Picker
            id="to-cost-centre"
            label="Cost centre"
            options={costCenters}
            currentName={current?.costCenterName}
            value={draft.toCostCenterId}
            onChange={(value) => set('toCostCenterId', value)}
          />
          <p className="text-xs text-[var(--color-muted)] sm:col-span-2">
            Leave a field on “No change” and it carries forward from the current assignment.
          </p>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-[var(--color-heading)]">Type</legend>
            <div className="flex gap-2">
              {(['PERMANENT', 'TEMPORARY'] as TransferType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  aria-pressed={draft.transferType === type}
                  onClick={() => {
                    set('transferType', type)
                    if (type === 'PERMANENT') set('plannedEndDate', null)
                  }}
                  className={cn(
                    'rounded-[var(--radius-control)] border px-3 py-1.5 text-sm transition-colors',
                    draft.transferType === type
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--color-body)]',
                  )}
                >
                  {type === 'PERMANENT' ? 'Permanent' : 'Temporary'}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="effective-from">Effective from</Label>
              <DatePicker
                id="effective-from"
                required
                value={draft.effectiveFrom}
                onChange={(event) => set('effectiveFrom', event.target.value)}
              />
              <p className="text-xs text-[var(--color-muted)]">
                A future date is fine — the move applies itself on the day.
              </p>
            </div>

            {draft.transferType === 'TEMPORARY' && (
              <div className="space-y-1.5">
                <Label htmlFor="planned-end">Returns on</Label>
                <DatePicker
                  id="planned-end"
                  required
                  min={draft.effectiveFrom}
                  value={draft.plannedEndDate ?? ''}
                  onChange={(event) => set('plannedEndDate', event.target.value || null)}
                />
                <p className="text-xs text-[var(--color-muted)]">
                  They go back automatically, so a secondment cannot quietly become permanent.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="transfer-reason">Reason</Label>
            <Input
              id="transfer-reason"
              maxLength={500}
              placeholder="Reorganisation, project assignment, promotion…"
              value={draft.reason ?? ''}
              onChange={(event) => set('reason', event.target.value || null)}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-body)]">
            <span className="font-medium text-[var(--color-heading)]">{employeeName}</span>
            {draft.transferType === 'TEMPORARY' ? ' moves temporarily from ' : ' moves from '}
            <DateValue value={draft.effectiveFrom} />
            {draft.plannedEndDate && (
              <>
                {' until '}
                <DateValue value={draft.plannedEndDate} />
              </>
            )}
            .
          </p>

          {nothingChanged ? (
            <p role="alert" className="text-sm text-[var(--color-danger)]">
              Nothing would change. Go back and pick at least one new placement.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {changes.map((change) => (
                <li key={change.label} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-28 text-[var(--color-muted)]">{change.label}</span>
                  <span className="text-[var(--color-muted)] line-through">{change.from ?? '—'}</span>
                  <ArrowRight className="size-3 text-[var(--color-muted)]" aria-hidden="true" />
                  <span className="font-medium text-[var(--color-heading)]">{change.to}</span>
                </li>
              ))}
            </ul>
          )}

          <CredentialPanel
            check={credentials.data}
            loading={credentials.isPending}
            canOverride={canOverride}
            onOverride={(failure) => { setOverrideError(null); setOverriding(failure) }}
          />

          <p className="text-sm text-[var(--color-body)]">
            This is raised as a request. It takes effect only once approved, and the employee's
            history keeps the placement they had before.
          </p>

          {error != null && <ErrorState error={error} />}
        </div>
      )}

      <OverrideDialog
        failure={overriding}
        context="TRANSFER"
        submitting={overrides.record.isPending}
        error={overrideError}
        onClose={() => setOverriding(null)}
        onConfirm={async (reason, expiresAt) => {
          if (!overriding) return
          try {
            // Recorded before the transfer is raised, not alongside it. The approval that
            // follows runs the same check again and will find this override waiting; doing
            // it the other way round would mean raising a request that cannot be approved.
            await overrides.record.mutateAsync({
              employeeId,
              credentialRuleId: overriding.ruleId,
              context: 'TRANSFER',
              reason,
              expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
            })
            setOverriding(null)
            await credentials.refetch()
          } catch (caught) {
            setOverrideError(toApiProblem(caught)?.detail ?? 'The override could not be recorded')
          }
        }}
      />

      <div className="mt-5 flex flex-wrap gap-2">
        {step > 0 && (
          <Button variant="outline" onClick={() => setStep((current) => current - 1)}>
            Back
          </Button>
        )}
        {step < STEPS.length - 1 && (
          <Button
            onClick={() => setStep((current) => current + 1)}
            disabled={step === 1 && (needsEndDate || endBeforeStart)}
            title={
              needsEndDate
                ? 'A temporary transfer needs a return date'
                : endBeforeStart
                  ? 'The return date must be after the start date'
                  : undefined
            }
          >
            Next
          </Button>
        )}
        {step === STEPS.length - 1 && (
          <Button
            onClick={() => onSubmit(draft)}
            disabled={nothingChanged || submitting || blocked || credentials.isPending}
            title={blocked ? 'A required document is missing or expired' : undefined}
          >
            {submitting ? 'Raising…' : 'Raise transfer'}
          </Button>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  )
}

function Picker({
  id,
  label,
  options,
  currentName,
  value,
  onChange,
}: {
  id: string
  label: string
  options: Option[]
  currentName: string | null | undefined
  value: string | null
  onChange: (value: string | null) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select
        id={id}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
        className="w-full"
      >
        <option value="">No change{currentName ? ` — ${currentName}` : ''}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </Select>
    </div>
  )
}

/** What the draft actually changes, for the confirmation step. */
function describeChanges(
  draft: TransferDraft,
  current: AssignmentView | undefined,
  lookups: {
    departments: Option[]
    locations: Option[]
    designations: Option[]
    grades: Option[]
    costCenters: Option[]
  },
): { label: string; from: string | null | undefined; to: string }[] {
  const nameOf = (options: Option[], id: string | null) =>
    options.find((option) => option.id === id)?.name ?? ''

  const candidates = [
    { label: 'Department', id: draft.toDepartmentId, from: current?.departmentName, options: lookups.departments },
    { label: 'Site', id: draft.toLocationId, from: current?.locationName, options: lookups.locations },
    { label: 'Designation', id: draft.toDesignationId, from: current?.designationName, options: lookups.designations },
    { label: 'Grade', id: draft.toGradeId, from: current?.gradeName, options: lookups.grades },
    { label: 'Cost centre', id: draft.toCostCenterId, from: current?.costCenterName, options: lookups.costCenters },
  ]

  return candidates
    .filter((candidate) => candidate.id !== null)
    .map((candidate) => ({
      label: candidate.label,
      from: candidate.from,
      to: nameOf(candidate.options, candidate.id),
    }))
}
